import {
  normalizeEventUuidList,
  normalizeProfileEventPartAssignments,
} from './memberEventPartUtils';
import { resolveAdminAffiliationName } from '../../../../utils/adminDisplayName';

export const firstNonEmpty = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim()) || '';

export const normalizeCareerTimeline = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => ({
      id: String(item?.id || `${item?.time_label || item?.timeLabel || ''}-${item?.title || ''}`),
      time_label: String(item?.time_label ?? item?.timeLabel ?? item?.year ?? '').trim(),
      title: String(item?.title ?? item?.label ?? '').trim(),
      note: String(item?.note ?? item?.memo ?? '').trim(),
    }))
    .filter((item) => item.time_label || item.title || item.note);
};

export const formatAdminName = (admin) => {
  if (!admin) return '未設定';
  return admin.display_name || admin.full_name || admin.nickname || admin.username || '未設定';
};

export const buildArtistAvatarStyle = ({
  pic,
  gender,
  buildProfileProxyUrl,
  joinPath,
  artistImageBasePath,
}) => {
  if (pic) {
    const url = buildProfileProxyUrl(artistImageBasePath, pic);
    return {
      backgroundImage: `url(${url || joinPath(artistImageBasePath, pic)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }

  let backgroundColor = 'var(--person-avatar-artist-unknown-bg)';
  if (gender === 'male') backgroundColor = 'var(--person-avatar-artist-male-bg)';
  else if (gender === 'female') backgroundColor = 'var(--person-avatar-artist-female-bg)';
  return {
    backgroundColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-inverse)',
    fontWeight: 700,
  };
};

export const buildAdminAvatarStyle = ({
  pic,
  buildProfileProxyUrl,
  joinPath,
  adminImageBasePath,
}) => {
  if (pic) {
    const url = buildProfileProxyUrl(adminImageBasePath, pic);
    return {
      backgroundImage: `url(${url || joinPath(adminImageBasePath, pic)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return {
    backgroundColor: 'var(--admin-avatar-fallback-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--admin-avatar-fallback-text)',
    fontWeight: 700,
  };
};

export const avatarInitialFromName = (name) => (name ? name.charAt(0) : '');

export const adminInitialFromName = (name) => {
  if (!name) return 'A';
  return name.trim().charAt(0) || 'A';
};

export const buildArtistProfileForModal = ({
  artist,
  resolveArtistMembershipLabel,
}) => {
  if (!artist) return null;
  const name = firstNonEmpty(artist.name, artist.artist_name);
  const nameEn = firstNonEmpty(artist.name_en, artist.artist_name_en, artist.nameEn);
  const partLabel = firstNonEmpty(artist.partLabel, artist.mainPartLabel, artist.part, artist.mainPart);
  return {
    id: artist.id || artist.artist_id || null,
    name,
    nameEn,
    // アバター色をパネルとモーダルで一致させるため gender を必ず引き継ぐ
    // （欠落すると PersonAvatar が unknown=オレンジ固定になる根本原因だった）。
    gender: firstNonEmpty(artist.gender, artist.artist_gender, artist.artistGender),
    initial: (name || '').trim().charAt(0) || '',
    pic: artist.pic || artist.artist_profile_picture || '',
    part: partLabel,
    isChief: !!artist.isChief || !!artist.is_chief,
    isConcertmaster: !!artist.isConcertmaster || !!artist.is_concertmaster,
    customRoles: Array.isArray(artist.custom_roles)
      ? artist.custom_roles
      : Array.isArray(artist.customRoles)
        ? artist.customRoles
        : [],
    instagram: artist.instagram_url || '',
    twitter: artist.twitter_url || '',
    facebook: artist.facebook_url || '',
    hp: artist.hp_url || artist.homepage_url || '',
    careerTimeline: normalizeCareerTimeline(
      artist.career_timeline || artist.careerTimeline || artist.artist_career_timeline
    ),
    comment: firstNonEmpty(
      artist.profile_one_liner,
      artist.one_liner,
      artist.comment,
      artist.bio,
      artist.notes
    ),
    eventMembershipLabel: firstNonEmpty(
      artist.eventMembershipLabel,
      artist.event_membership_label,
      resolveArtistMembershipLabel?.(artist)
    ),
    eventPartLabel: firstNonEmpty(
      artist.eventPartLabel,
      artist.event_part_label
    ),
    eventUuids: normalizeEventUuidList(artist.eventUuids || artist.event_uuids),
    eventPartAssignments: normalizeProfileEventPartAssignments(
      artist.eventPartAssignments || artist.event_part_assignments
    ),
    groupEvents: Array.isArray(artist.groupEvents)
      ? artist.groupEvents
      : (Array.isArray(artist.group_events) ? artist.group_events : []),
    isUnifiedEventGroupChat:
      artist.isUnifiedEventGroupChat === true || artist.is_unified_event_group_chat === true,
  };
};

export const buildAdminProfileForModal = ({
  admin,
  resolveAdminMembershipLabel,
}) => {
  if (!admin) return null;
  const externalAffiliationName = firstNonEmpty(
    admin.externalAffiliationName,
    admin.external_affiliation_name
  );
  return {
    adminUserId: admin.adminUserId || admin.admin_user_id || admin.adminId || admin.admin_id || admin.user_id || admin.id || null,
    name: formatAdminName(admin),
    nickname: admin.nickname || admin.display_name_sub || '',
    companyName: firstNonEmpty(
      admin.affiliationName,
      admin.affiliation_name,
      admin.companyName,
      admin.company_name,
      resolveAdminAffiliationName({
        role: admin.role,
        isExternalAccount: admin.isExternalAccount === true || admin.is_external_account === true,
        externalAffiliationName,
      })
    ),
    position: admin.position || '',
    department: admin.department || '',
    comment: firstNonEmpty(admin.profile_one_liner, admin.comment, admin.note),
    pic: admin.profile_picture || '',
    instagram: firstNonEmpty(admin.instagram_url, admin.admin_instagram_url),
    twitter: firstNonEmpty(admin.twitter_url, admin.admin_twitter_url),
    facebook: firstNonEmpty(admin.facebook_url, admin.admin_facebook_url),
    youtube: firstNonEmpty(admin.youtube_url, admin.admin_youtube_url),
    hp: firstNonEmpty(admin.hp_url, admin.admin_hp_url, admin.homepage_url),
    eventMembershipLabel: firstNonEmpty(
      admin.eventMembershipLabel,
      admin.event_membership_label,
      resolveAdminMembershipLabel?.(admin)
    ),
  };
};

export const buildConciergeMembersForModal = ({
  isConciergeEvent,
  conciergePlanner,
  buildArtistProfile,
  resolveConciergeMembershipLabel,
}) => {
  if (!isConciergeEvent || !conciergePlanner) return [];
  const name = firstNonEmpty(
    conciergePlanner.artist_name,
    conciergePlanner.name,
    conciergePlanner.display_name,
    conciergePlanner.full_name
  ) || '企画者';
  const key = conciergePlanner.artist_id ?? conciergePlanner.id ?? name;
  const payload = buildArtistProfile({
    ...conciergePlanner,
    artist_name: name,
    name,
    partLabel: '企画者',
    part: '企画者',
  });
  return [{
    key: `concierge-${key}`,
    name,
    pic: conciergePlanner.artist_profile_picture || conciergePlanner.profile_picture || '',
    gender: conciergePlanner.artist_gender || conciergePlanner.gender || null,
    payload,
    eventMembershipLabel: resolveConciergeMembershipLabel?.(conciergePlanner) || '',
  }];
};

export const buildMemberModalProfileState = ({
  isConciergeEvent,
  conciergePlanner,
  resolveArtistMembershipLabel,
  resolveAdminMembershipLabel,
  resolveConciergeMembershipLabel,
}) => {
  const buildArtistProfile = (artist) => buildArtistProfileForModal({
    artist,
    resolveArtistMembershipLabel,
  });
  const buildAdminProfile = (admin) => buildAdminProfileForModal({
    admin,
    resolveAdminMembershipLabel,
  });
  const conciergeMembers = buildConciergeMembersForModal({
    isConciergeEvent,
    conciergePlanner,
    buildArtistProfile,
    resolveConciergeMembershipLabel,
  });
  return {
    buildArtistProfile,
    buildAdminProfile,
    conciergeMembers,
  };
};
